import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";
import GridView from "components/GridView";
import useGridStore from "components/GridView/grid.store";
import { ExaminersService } from "services/examiners";
import { useNotification } from "hooks/useNotification";
import { GridSchema, GridViewHeaderProps } from "components/GridView/GridView.types";
import useSystemDateTheme from "hooks/useSystemDateTheme";
import IconButton from "components/blocks/IconButton";
import ActionModal from "components/blocks/Modal/ActionModal";
import { AccountService } from "services/accountService";
import { ExaminerUserResult } from "services/examiners/Model/ExaminerTypes";
import { useCookieContext } from "contexts";
import useScreenSize from "hooks/useScreenSize";
import { prepareFilters } from "components/GridView/components/GridFilter";
import AuthGuard from "components/blocks/AuthGuard";

export default function ExaminersGridView() {
  const { t } = useTranslation("Examiners");
  const { isAr } = useCookieContext();
  const { toSystemThemeDateFormat } = useSystemDateTheme();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [examiners, setExaminers] = useState<ExaminerUserResult[] | undefined>();
  const [totalCount, setTotalCount] = useState(0);
  const ready = useRef(false);
  const screenSize = useScreenSize();

  const [modalProps, setModalProps] = useState({
    isOpen: false,
    type: "block",
    id: "",
    examinerName: "",
    loading: false,
  });

  const { sortState, pagination, setPagination, searchValue, filters, resetGridStore } = useGridStore(
    useShallow((state) => ({
      sortState: state.sortState,
      pagination: state.pagination,
      setPagination: state.setPagination,
      searchValue: state.generalSearch,
      filters: state.filters,
      resetGridStore: state.reset,
    })),
  );

  const fetchExaminers = async ({ resetPagination = false }: { resetPagination?: boolean } = {}) => {
    try {
      setLoading(true);
      const response = await ExaminersService.getAllExaminers({
        pageIndex: resetPagination ? 1 : pagination.currentPage,
        pageSize: 10,
        search: searchValue,
        sort: sortState
          ? Object.keys(sortState).map((key) => ({
              field: key,
              dir: sortState[key],
            }))
          : [],
        filter: prepareFilters(filters),
      });

      setExaminers(response.data.result);
      setTotalCount(response.data.totalCount);

      if (resetPagination) {
        setPagination({
          currentPage: 1,
          totalPages: Math.ceil(response.data.totalCount / 10),
        });
      }
    } catch (error) {
      showNotification({
        type: "error",
        description: t("Common:internalServerError"),
      });
      setPagination({
        currentPage: 1,
        totalPages: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ready.current) {
      return;
    }
    fetchExaminers();
  }, [pagination.currentPage]);

  useEffect(() => {
    if (!ready.current) {
      return;
    }
    fetchExaminers({ resetPagination: true });
  }, [sortState, searchValue, filters, isAr]);

  useEffect(() => {
    if (!ready.current) {
      resetGridStore();
      ready.current = true;
      return;
    }
  }, [resetGridStore]);

  const onChangeExaminerStatus = (isUserBlocked: boolean, name: string, id: string) => {
    // showModal
    setModalProps({
      isOpen: true,
      type: !isUserBlocked ? "block" : "unblock",
      examinerName: name,
      id: id,
      loading: false,
    });
  };

  const onModalConfirm = async () => {
    setModalProps({
      ...modalProps,
      loading: true,
    });

    try {
      const response =
        modalProps.type === "block"
          ? await AccountService.blockUser(modalProps.id)
          : await AccountService.unblockUser(modalProps.id);

      if (response.hasError) {
        showNotification({
          type: "error",
          description: response.description,
        });
        setModalProps({
          ...modalProps,
          loading: false,
        });
        return;
      }

      // TODO: check with @Alaa
      // introduce a 2 seconds delay (somehow the data is not updated immediately)
      await new Promise((resolve) => setTimeout(resolve, 2000));

      await fetchExaminers({ resetPagination: true });

      setModalProps({
        isOpen: false,
        type: modalProps.type,
        examinerName: "",
        id: "",
        loading: false,
      });

      showNotification({
        type: "success",
        description: t("Common:success"),
      });
    } catch (error) {
      showNotification({
        type: "error",
        description: t("Common:internalServerError"),
      });
      setModalProps({
        ...modalProps,
        loading: false,
      });
    }
  };

  const onExportClicked = async () => {
    setLoading(true);
    try {
      const response = await ExaminersService.exportExaminersToExcelAsync({
        pageIndex: pagination.currentPage,
        pageSize: 10,
        search: searchValue,
      });
      const blob = new Blob([response.data], { type: response.headers["content-type"] });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = "Examiners.xlsx";
      document.body.appendChild(link);
      link.click();
      setLoading(false);
      link.remove();
    } catch (error) {
      showNotification({
        type: "error",
        description: t("Common:internalServerError"),
      });
      setLoading(false);
    }
  };

  const gridSchema = useMemo<GridSchema[]>(
    () => [
      {
        field: `Examiner.${isAr ? "ArabicName" : "EnglishName"}`,
        displayName: t("examinerName"),
        showOnMobile: true,
        sortable: true,
        filterable: true,
        render: (row: ExaminerUserResult) => {
          return <Link to={`view?examinerId=${row.id}`}>{isAr ? row.arabicName : row.englishName}</Link>;
        },
      },
      {
        field: "Examiner.Username",
        accessor: "username",
        filterable: true,
        sortable: true,
        displayName: t("username"),
      },
      {
        field: "TaskCount",
        accessor: "taskCount",
        displayName: t("completedTasks"),
      },
      {
        field: "CreatedOn",
        displayName: t("createdOn"),
        sortable: true,
        filterable: true,
        filterOptions: {
          type: "date",
        },
        render(row: ExaminerUserResult) {
          return <> {row.createdOn ? toSystemThemeDateFormat(row.createdOn) : ""}</>;
        },
      },
      {
        field: "IsActive",
        displayName: screenSize === "mobile" ? t("Common:status") : t("status"),
        showOnMobile: true,
        sortable: true,
        // sortable: true,
        // filterable: true,
        // filterOptions: {
        //   type: "select",
        //   options: [
        //     { id: "0", value: t("active") },
        //     { id: "1", value: t("blocked") },
        //   ],
        // },
        render(row: ExaminerUserResult) {
          const statusClassName = row.isActive
            ? "bg-primary bg-opacity-10 text-primary"
            : "bg-danger bg-opacity-10 text-danger";
          return (
            <span className={`badge rounded-4 ${statusClassName} py-2`}>{t(row.isActive ? "active" : "blocked")}</span>
          );
        },
      },
      {
        field: "actions",
        displayName: t("Common:actions"),
        render: (row: ExaminerUserResult) => {
          const innerIconColor = row.isActive ? "danger" : "dark";

          return (
            <AuthGuard requiredRoles={["FundingExaminersInfoWrite"]}>
              <IconButton
                icon="icon-block"
                fitToIconWidth
                innerIconColor={innerIconColor}
                size="sm"
                title={row.isActive ? t("Common:block") : t("Common:unblock")}
                onClick={() => {
                  onChangeExaminerStatus(!row.isActive, isAr ? row.arabicName : row.englishName, row.id);
                }}
              />
            </AuthGuard>
          );
        },
      },
    ],
    [t, toSystemThemeDateFormat],
  );

  const viewHeader: GridViewHeaderProps = {
    title: t("examinersInfo"),
    singularName: t("examiner"),
    totalCount: totalCount,
    hideAddButton: false,
    onExportClick: onExportClicked,
  };

  return (
    <div>
      <GridView
        loading={loading}
        gridProps={{
          data: examiners ?? [],
          gridSchema,
        }}
        viewHeaderProps={viewHeader}
      />
      <ActionModal
        isOpen={modalProps.isOpen}
        onClose={() => {
          setModalProps({
            isOpen: false,
            type: modalProps.type,
            examinerName: "",
            id: "",
            loading: false,
          });
        }}
        headerMsg={modalProps.type === "block" ? t("blockExaminerModalHeaderMsg") : t("unblockExaminerModalHeaderMsg")}
        subMsg={
          modalProps.type === "block"
            ? t("blockExaminerModalSubMsg", {
                examinerName: modalProps.examinerName,
              })
            : t("unblockExaminerModalSubMsg", {
                examinerName: modalProps.examinerName,
              })
        }
        actionIcon="icon-block"
        confirmBtnText={modalProps.type === "block" ? t("block") : t("unblock")}
        iconWrapperColor={modalProps.type === "block" ? "danger" : "primary"}
        onActionConfirm={onModalConfirm}
        loading={modalProps.loading}
      />
    </div>
  );
}
